import React, { useEffect, useState } from 'react';
import { createUseStyles } from '../../../../../../theming';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import Site, { CreateSiteOptions } from '../../../../../../interfaces/api/Site';
import { Controller, useForm } from 'react-hook-form';
import useSite from '../../../../../../hooks/api/useSite';
import CustomerSelect, { CustomerOption } from './CustomerSelect';
import useCustomer from '../../../../../../hooks/api/useCustomer';

const useStyles = createUseStyles({
  root: {},
  title: {
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
  tuto: {
    marginBottom: 15,
  },
});

interface Props {
  legacyCustomer: {
    id: number;
    name: string;
    address: string;
    city: string;
    siret: string;
    phoneNumber: string;
    fax: string;
  };
  open: boolean;
  onClose: () => void;
  onMigrateSiteDone: (site: Site) => void;
}

type CreateSiteWithNewCustomer = CreateSiteOptions & { newCustomerName?: string };

const MigrateCustomerDialog: React.FC<Props> = ({ legacyCustomer, onMigrateSiteDone, open, onClose }) => {
  const classes = useStyles();
  const { createSite } = useSite();
  const { create: createCustomer } = useCustomer();
  const [formError, setFormError] = useState<string | undefined>();
  const [customer, setCustomer] = useState<CustomerOption | null>();

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<CreateSiteWithNewCustomer>({
    defaultValues: {
      socialReason: legacyCustomer.name,
      address: legacyCustomer.address,
      postalCode: '',
      city: legacyCustomer.city,
      siret: legacyCustomer.siret,
      phoneNumber: legacyCustomer.phoneNumber,
      customerId: '',
      legacyCustomerId: legacyCustomer.id,
    },
  });

  const handleChangeCustomer = (event, option?: CustomerOption | null) => {
    setCustomer(option);

    const isNewCustomer = option?.value === 'new';
    if (isNewCustomer) {
      setValue('customerId', 'Inscrivez le nom du nouveau client');
    } else {
      setValue('customerId', option?.value || '');
    }
  };

  const onSubmit = async (siteOptions: CreateSiteWithNewCustomer) => {
    const { newCustomerName, ...site } = siteOptions;

    try {
      setFormError(undefined);

      if (newCustomerName) {
        const customer = await createCustomer({ name: newCustomerName });
        site.customerId = customer.id;
      }

      const newSite = await createSite(site);
      onMigrateSiteDone(newSite);
    } catch (e) {
      setFormError(e.message);
    }
  };

  const addNewCustomer = customer?.value === 'new';

  return (
    <Dialog open={open} onClose={onClose} scroll='paper'>
      <DialogTitle className={classes.title}>Information du site</DialogTitle>

      <DialogContent dividers>
        <div className={classes.tuto}>
          Dans la version actuelle de Scaleed, il y a un site pour un client.
          <br />
          Dans la nouvelle version, nous aurons plusieurs sites par client.
          <br />
          <br />
          <u>Exemple:</u>
          <br />
          Actuellement, il y a par exemple un client "King Jouet Meylan" et un autre client "King Jouet Lyon". Chacun
          d'eux a un site qui lui est propre.
          <br />
          Dans la nouvelle version il faudra avoir un unique client "King Jouet" avec le site "King Jouet Meylan" et
          "King Jouet Lyon".
          <br />
        </div>

        {addNewCustomer ? (
          <Controller
            name='newCustomerName'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Nom du nouveau client *'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        ) : (
          <Controller
            name='customerId'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ fieldState: { error } }) => (
              <CustomerSelect className={classes.formControl} error={error?.message} onChange={handleChangeCustomer} />
            )}
          />
        )}

        <Controller
          name='socialReason'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Raison sociale *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='siret'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Siret *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='address'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Adresse *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='postalCode'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Code postal *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='city'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Ville *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='phoneNumber'
          control={control}
          rules={{ required: { value: true, message: 'Le champ est requis' } }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Téléphone *'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name='clientCode'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              className={classes.formControl}
              label='Code client'
              fullWidth
              error={undefined !== error}
              helperText={error?.message}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Migrer {isSubmitting && <CircularProgress size={15} />}
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default MigrateCustomerDialog;
